import React, { useContext, useState } from "react"
import { Button, Div, Label, Text } from "atomize"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faRightLeft, faXmark } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import CardInset from "./CardInset"
import Switch from "../atoms/Switch"
import Divider from "../atoms/Divider"
import { editParticipantPresence, setLessonSubstitute } from "../utils/firestore.utils"
import {
	getLessonColor,
	getLessonFillColor,
	getPaymentMethodName,
	isPlaceholderPartecipant,
} from "../utils/func.utils"
import { isSubstitute, substituteOf } from "../utils/substitutes.utils"
import { paidAtSchool, PAID_TO_SCHOOL_LABEL } from "../utils/billing.utils"
import { sharedDataContext } from "../providers/sharedData.provider"
import { modalContext } from "../providers/modal.provider"
import DashedOutline from "../atoms/DashedOutline"
import HelpTip from "../help/HelpTip"

function LessonPresence({ lessonData }) {
	const Modal = useContext(modalContext)
	const { getLessonType, getPaymentMethod } = useContext(sharedDataContext)
	// il posto nella lezione serve a chi salva: è quello che distingue
	// chi non ha un ID, e l'elenco qui sotto è filtrato. I sostituti non
	// hanno una presenza da segnare: stanno sotto a chi sostituiscono.
	const subscribers = (lessonData.partecipants || [])
		.map((player, index) => ({ player, index }))
		.filter(
			({ player }) =>
				player.paymentMethod === "subscription" &&
				!isSubstitute(player) &&
				// un posto ancora senza nome non ha nessuno da segnare
				!isPlaceholderPartecipant(player),
		)
	// il posto dell'allievo che si sta salvando: un secondo tocco nel
	// frattempo ripartirebbe dallo stato vecchio e lo annullerebbe
	const [savingIndex, setSavingIndex] = useState(null)

	const togglePresence = async (player, index) => {
		if (savingIndex !== null) return
		setSavingIndex(index)
		// senza il campo l'allievo è presente: così nasce la lezione
		const isPresent = player.isPresent === false
		try {
			await editParticipantPresence(
				lessonData.lessonID,
				{ playerID: player.playerID, index },
				isPresent,
			)
		} catch {
			toast.error(`Non sono riuscito a segnare la presenza di ${player.playerName}`, {
				toastId: "presence-not-saved",
				position: "top-center",
			})
			setSavingIndex(null)
			return
		}

		// Chi torna presente non ha più bisogno di un sostituto: resterebbe
		// nella lezione con il suo recupero o il suo pagamento, per un posto
		// che non c'è.
		if (isPresent && substituteOf(lessonData.partecipants, player)) {
			try {
				await setLessonSubstitute(lessonData.lessonID, player, null)
			} catch {
				toast.error("Presenza segnata, ma il sostituto è rimasto: toglilo dalla ✕", {
					toastId: "substitute-not-removed",
					position: "top-center",
				})
			}
		}
		setSavingIndex(null)
	}

	const removeSubstitute = async (absent, index) => {
		if (savingIndex !== null) return
		setSavingIndex(index)
		try {
			await setLessonSubstitute(lessonData.lessonID, absent, null)
		} catch {
			toast.error("Non sono riuscito a togliere il sostituto", {
				toastId: "substitute-not-removed",
				position: "top-center",
			})
		} finally {
			setSavingIndex(null)
		}
	}

	if (subscribers.length === 0) return null

	const lessonColor = getLessonColor(getLessonType(lessonData.lessonType))
	const lessonDarkColor = getLessonColor(getLessonType(lessonData.lessonType), true)

	return (
		<CardInset>
			<HelpTip
				tip="presenze"
				justify="center"
				color={getLessonFillColor(getLessonType(lessonData.lessonType))}
				m={{ b: "0.5rem", x: "1rem" }}>
				<Text>Presenze abbonati:</Text>
			</HelpTip>
			<Div bg={theme.colors.background} rounded={theme.rounded["20px"]} p={{ y: "0.5rem" }}>
				{subscribers.map(({ player, index }, i) => {
					const isAbsent = player.isPresent === false
					const substitute = substituteOf(lessonData.partecipants, player)

					return (
						// la chiave sta sul fragment, che è quello che l'elenco
						// contiene davvero, e porta anche il posto: due allievi
						// con lo stesso ID non devono diventare la stessa riga
						<React.Fragment key={`${player.playerID || "player"}-${i}`}>
							<Label
								onClick={() => togglePresence(player, index)}
								display="flex"
								justify="space-between"
								align="center"
								bg="background"
								p={{ x: "1rem", y: "0.5rem" }}
								rounded="20px">
								<Text>{player.playerName}</Text>
								<Switch
									m={{ l: "0.5rem" }}
									shadow="0"
									checked={!isAbsent}
									isLoading={savingIndex === index}
									activeColor="gunmetal_fill"
									inactiveColor="gray_light"
									text={isAbsent ? "A" : "P"}
								/>
							</Label>

							{substitute ? (
								<Div
									m={{ x: "1rem", b: "0.5rem" }}
									p={{ l: "1rem", r: "0.25rem", y: "0.25rem" }}
									bg="surface"
									rounded="15px"
									d="flex"
									align="center"
									style={{ gap: "0.625rem" }}>
									<FontAwesomeIcon icon={faRightLeft} color={lessonDarkColor} />
									<Div flexGrow="1">
										<Text textWeight="500">{substitute.playerName}</Text>
										<Text textSize="caption" textColor={theme.colors.text_light}>
											Sostituto ·{" "}
											{paidAtSchool(substitute, lessonData)
												? PAID_TO_SCHOOL_LABEL.toLowerCase()
												: getPaymentMethodName(
														getPaymentMethod,
														substitute.paymentMethod,
													)}
										</Text>
									</Div>
									<Button
										h="2.75rem"
										w="2.75rem"
										p="0"
										d="flex"
										align="center"
										justify="center"
										bg="transparent"
										aria-label={`Togli ${substitute.playerName}`}
										onClick={() => removeSubstitute(player, index)}>
										<FontAwesomeIcon icon={faXmark} color={theme.colors.red} />
									</Button>
								</Div>
							) : (
								isAbsent && (
									<Div m={{ x: "1rem", b: "0.5rem" }}>
										<Button
											w="100%"
											h="2.75rem"
											bg="transparent"
											rounded="15px"
											textColor={theme.colors.gunmetal}
											pos="relative"
											style={{ gap: "0.5rem" }}
											onClick={() =>
												Modal.open("addSubstitute", {
													lessonData,
													absent: player,
												})
											}>
											<DashedOutline color={lessonDarkColor} width={2} radius={15} />
											<FontAwesomeIcon icon={faPlus} />
											<Text tag="span" textWeight="500">
												Aggiungi sostituto
											</Text>
										</Button>
									</Div>
								)
							)}

							{i < subscribers.length - 1 && (
								<Divider w="2px solid" borderColor={lessonColor} />
							)}
						</React.Fragment>
					)
				})}
			</Div>
		</CardInset>
	)
}

export default LessonPresence
